/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  StatusBar,
  Alert,
  Dimensions,
  TextInput,
  FlatList,
  Modal,
  RefreshControl,
  ActivityIndicator,
  Platform,
  Linking,
  Share,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo from '@react-native-community/netinfo';
import {
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { SafeAreaView } from 'react-native-safe-area-context';

const { width } = Dimensions.get('window');

const JobBoard = () => {
  const navigation = useNavigation();
  const [isOnline, setIsOnline] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [selectedField, setSelectedField] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedExperience, setSelectedExperience] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [savedJobs, setSavedJobs] = useState([]);
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  const searchTimeoutRef = useRef(null);
  const flatListRef = useRef(null);

  const fields = [
    { id: 'all', label: 'All Fields', icon: 'grid' },
    { id: 'software', label: 'Software', icon: 'code-braces' },
    { id: 'mechanical', label: 'Mechanical', icon: 'cog' },
    { id: 'civil', label: 'Civil', icon: 'bridge' },
    { id: 'electrical', label: 'Electrical', icon: 'flash' },
    { id: 'chemical', label: 'Chemical', icon: 'flask' },
    { id: 'aerospace', label: 'Aerospace', icon: 'airplane' },
    { id: 'biomedical', label: 'Biomedical', icon: 'heart-pulse' },
    { id: 'data', label: 'Data Science', icon: 'chart-bar' },
    { id: 'management', label: 'Management', icon: 'briefcase' },
    { id: 'research', label: 'Research', icon: 'microscope' },
  ];

  const locations = [
    { id: 'all', label: 'All Locations' },
    { id: 'remote', label: 'Remote' },
    { id: 'hybrid', label: 'Hybrid' },
    { id: 'uk', label: 'UK' },
    { id: 'usa', label: 'USA' },
    { id: 'germany', label: 'Germany' },
    { id: 'canada', label: 'Canada' },
    { id: 'australia', label: 'Australia' },
    { id: 'london', label: 'London' },
    { id: 'manchester', label: 'Manchester' },
    { id: 'new-york', label: 'New York' },
    { id: 'san-francisco', label: 'San Francisco' },
  ];

  const experiences = [
    { id: 'all', label: 'Any Experience' },
    { id: 'entry', label: 'Entry Level (0-2 yrs)' },
    { id: 'mid', label: 'Mid Level (2-5 yrs)' },
    { id: 'senior', label: 'Senior (5+ yrs)' },
    { id: 'lead', label: 'Lead/Manager' },
  ];

  const types = [
    { id: 'all', label: 'All Types' },
    { id: 'full-time', label: 'Full Time' },
    { id: 'part-time', label: 'Part Time' },
    { id: 'contract', label: 'Contract' },
    { id: 'freelance', label: 'Freelance' },
    { id: 'temporary', label: 'Temporary' },
  ];

  const [jobs, setJobs] = useState([
    {
      id: 'JOB001',
      title: 'Senior Software Engineer',
      company: 'Google',
      logo: 'https://cdn-icons-png.flaticon.com/512/300/300221.png',
      location: 'London, UK',
      field: 'software',
      type: 'full-time',
      experience: 'senior',
      salary: '£95,000-£120,000',
      applicationDeadline: '2024-03-31',
      postedDate: '2024-02-15',
      description: 'Join Google\'s engineering team to work on cutting-edge cloud computing and machine learning projects.',
      requirements: [
        'BS/MS in Computer Science or related field',
        '5+ years of software development experience',
        'Expertise in Java/Python/Go',
        'Experience with distributed systems',
        'Strong problem-solving skills'
      ],
      responsibilities: [
        'Design and implement scalable software solutions',
        'Lead technical discussions and architecture decisions',
        'Mentor junior engineers',
        'Collaborate with cross-functional teams',
        'Ensure code quality and best practices'
      ],
      benefits: [
        'Competitive salary and bonuses',
        'Stock options',
        'Comprehensive health insurance',
        'Flexible work hours',
        'Professional development budget',
        'On-site amenities'
      ],
      applyLink: 'https://careers.google.com',
      contactEmail: 'careers@google.com',
      spots: 5,
      applications: 850,
      featured: true,
      urgent: false,
    },
    {
      id: 'JOB002',
      title: 'Mechanical Design Engineer',
      company: 'Rolls-Royce',
      logo: 'https://cdn-icons-png.flaticon.com/512/841/841364.png',
      location: 'Derby, UK',
      field: 'mechanical',
      type: 'full-time',
      experience: 'mid',
      salary: '£55,000-£70,000',
      applicationDeadline: '2024-04-15',
      postedDate: '2024-02-10',
      description: 'Design and develop mechanical systems for aerospace applications.',
      requirements: [
        'Degree in Mechanical Engineering',
        '3+ years of CAD design experience',
        'Proficiency in SolidWorks/CATIA',
        'Knowledge of thermodynamics and fluid mechanics',
        'Understanding of manufacturing processes'
      ],
      responsibilities: [
        'Create detailed mechanical designs',
        'Perform stress and thermal analysis',
        'Create technical documentation',
        'Collaborate with manufacturing teams',
        'Participate in design reviews'
      ],
      benefits: [
        'Competitive salary',
        'Company pension',
        'Private healthcare',
        'Professional development',
        'Travel opportunities'
      ],
      applyLink: 'https://careers.rolls-royce.com',
      contactEmail: 'careers@rolls-royce.com',
      spots: 3,
      applications: 320,
      featured: true,
      urgent: true,
    },
    {
      id: 'JOB003',
      title: 'Civil Engineering Project Manager',
      company: 'Arup',
      logo: 'https://cdn-icons-png.flaticon.com/512/3220/3220315.png',
      location: 'Manchester, UK',
      field: 'civil',
      type: 'full-time',
      experience: 'senior',
      salary: '£65,000-£85,000',
      applicationDeadline: '2024-03-25',
      postedDate: '2024-02-05',
      description: 'Lead sustainable infrastructure projects from conception to completion.',
      requirements: [
        'Degree in Civil Engineering',
        'Professional Engineer certification',
        '7+ years of project management experience',
        'Experience with sustainable design',
        'Strong leadership skills'
      ],
      responsibilities: [
        'Manage project timelines and budgets',
        'Coordinate with clients and stakeholders',
        'Oversee design and construction teams',
        'Ensure compliance with regulations',
        'Manage risk and quality control'
      ],
      benefits: [
        'Competitive package',
        'Performance bonus',
        'Flexible working',
        'Health and wellness programs',
        'Continuous learning opportunities'
      ],
      applyLink: 'https://www.arup.com/careers',
      contactEmail: 'careers@arup.com',
      spots: 2,
      applications: 210,
      featured: true,
      urgent: false,
    },
    {
      id: 'JOB004',
      title: 'Electrical Systems Engineer',
      company: 'Siemens',
      logo: 'https://cdn-icons-png.flaticon.com/512/5968/5968350.png',
      location: 'Berlin, Germany',
      field: 'electrical',
      type: 'full-time',
      experience: 'mid',
      salary: '€65,000-€80,000',
      applicationDeadline: '2024-04-10',
      postedDate: '2024-02-12',
      description: 'Work on smart grid technology and renewable energy systems.',
      requirements: [
        'Degree in Electrical Engineering',
        '3+ years of power systems experience',
        'Knowledge of SCADA systems',
        'Experience with renewable energy',
        'German language skills preferred'
      ],
      responsibilities: [
        'Design electrical systems',
        'Conduct system analysis',
        'Develop control algorithms',
        'Create technical specifications',
        'Support field implementation'
      ],
      benefits: [
        'International work environment',
        'Language courses',
        'Relocation assistance',
        '30 days vacation',
        'Company car option'
      ],
      applyLink: 'https://new.siemens.com/global/en/company/jobs.html',
      contactEmail: 'careers@siemens.com',
      spots: 4,
      applications: 180,
      featured: false,
      urgent: true,
    },
    {
      id: 'JOB005',
      title: 'Data Scientist',
      company: 'Amazon',
      logo: 'https://cdn-icons-png.flaticon.com/512/731/731985.png',
      location: 'Remote',
      field: 'data',
      type: 'full-time',
      experience: 'mid',
      salary: '$120,000-$150,000',
      applicationDeadline: '2024-03-20',
      postedDate: '2024-02-01',
      description: 'Develop machine learning models for e-commerce optimization.',
      requirements: [
        'Masters/PhD in Data Science or related',
        '3+ years of ML experience',
        'Expert Python/R programming',
        'Strong statistical background',
        'Experience with big data tools'
      ],
      responsibilities: [
        'Develop predictive models',
        'Analyze large datasets',
        'Create data visualizations',
        'Collaborate with product teams',
        'Present insights to stakeholders'
      ],
      benefits: [
        'Full remote work',
        'Stock options',
        'Comprehensive benefits',
        'Learning budget',
        'Amazon discounts'
      ],
      applyLink: 'https://www.amazon.jobs',
      contactEmail: 'data-jobs@amazon.com',
      spots: 6,
      applications: 1250,
      featured: true,
      urgent: false,
    },
    {
      id: 'JOB006',
      title: 'Process Engineer',
      company: 'BP',
      logo: 'https://cdn-icons-png.flaticon.com/512/3208/3208726.png',
      location: 'Houston, USA',
      field: 'chemical',
      type: 'full-time',
      experience: 'entry',
      salary: '$75,000-$90,000',
      applicationDeadline: '2024-03-15',
      postedDate: '2024-01-25',
      description: 'Optimize refining processes for efficiency and safety.',
      requirements: [
        'Degree in Chemical Engineering',
        '0-2 years of experience',
        'Knowledge of process simulation',
        'Understanding of safety protocols',
        'Strong analytical skills'
      ],
      responsibilities: [
        'Monitor process performance',
        'Identify optimization opportunities',
        'Develop process improvements',
        'Ensure safety compliance',
        'Create technical reports'
      ],
      benefits: [
        'Entry-level training program',
        'Mentorship opportunities',
        'Competitive salary',
        'Health benefits',
        'Relocation package'
      ],
      applyLink: 'https://www.bp.com/en/global/corporate/careers.html',
      contactEmail: 'earlycareers@bp.com',
      spots: 8,
      applications: 450,
      featured: false,
      urgent: true,
    },
    {
      id: 'JOB007',
      title: 'Aerospace Structures Engineer',
      company: 'Airbus',
      logo: 'https://cdn-icons-png.flaticon.com/512/809/809957.png',
      location: 'Toulouse, France',
      field: 'aerospace',
      type: 'contract',
      experience: 'senior',
      salary: '€70,000-€90,000',
      applicationDeadline: '2024-04-05',
      postedDate: '2024-02-08',
      description: 'Design and analyze aircraft structural components.',
      requirements: [
        'Degree in Aerospace Engineering',
        '5+ years of structures experience',
        'Proficiency in FEM analysis',
        'Knowledge of aerospace materials',
        'French language proficiency'
      ],
      responsibilities: [
        'Structural design and analysis',
        'FEM modeling and simulation',
        'Stress and fatigue analysis',
        'Technical documentation',
        'Cross-team collaboration'
      ],
      benefits: [
        'Contract with extension option',
        'European travel opportunities',
        'Professional development',
        'Language training',
        'Competitive daily rate'
      ],
      applyLink: 'https://www.airbus.com/en/careers',
      contactEmail: 'contractors@airbus.com',
      spots: 3,
      applications: 290,
      featured: true,
      urgent: false,
    },
    {
      id: 'JOB008',
      title: 'Biomedical Device Engineer',
      company: 'Medtronic',
      logo: 'https://cdn-icons-png.flaticon.com/512/2785/2785352.png',
      location: 'Minneapolis, USA',
      field: 'biomedical',
      type: 'full-time',
      experience: 'mid',
      salary: '$90,000-$110,000',
      applicationDeadline: '2024-03-30',
      postedDate: '2024-02-03',
      description: 'Develop innovative medical devices for patient care.',
      requirements: [
        'Degree in Biomedical Engineering',
        '3+ years of medical device experience',
        'Knowledge of FDA regulations',
        'Experience with CAD design',
        'Strong problem-solving skills'
      ],
      responsibilities: [
        'Device design and development',
        'Prototype testing and validation',
        'Regulatory documentation',
        'Clinical trial support',
        'Quality assurance'
      ],
      benefits: [
        'Healthcare benefits',
        'Stock options',
        'Research opportunities',
        'Flexible schedule',
        'On-site gym'
      ],
      applyLink: 'https://www.medtronic.com/us-en/careers.html',
      contactEmail: 'device-jobs@medtronic.com',
      spots: 4,
      applications: 320,
      featured: false,
      urgent: true,
    },
    {
      id: 'JOB009',
      title: 'Engineering Manager',
      company: 'Tesla',
      logo: 'https://cdn-icons-png.flaticon.com/512/5968/5968313.png',
      location: 'Austin, USA',
      field: 'management',
      type: 'full-time',
      experience: 'lead',
      salary: '$140,000-$180,000',
      applicationDeadline: '2024-04-20',
      postedDate: '2024-02-14',
      description: 'Lead engineering teams developing sustainable energy solutions.',
      requirements: [
        'Degree in Engineering',
        '8+ years of engineering experience',
        '3+ years of management experience',
        'Strong leadership skills',
        'Experience in automotive/energy sector'
      ],
      responsibilities: [
        'Lead engineering teams',
        'Manage project delivery',
        'Develop technical strategy',
        'Mentor team members',
        'Interface with executives'
      ],
      benefits: [
        'Competitive salary + bonus',
        'Tesla stock options',
        'Comprehensive benefits',
        'EV charging at work',
        'Innovation-focused culture'
      ],
      applyLink: 'https://www.tesla.com/careers',
      contactEmail: 'engineering-careers@tesla.com',
      spots: 2,
      applications: 680,
      featured: true,
      urgent: false,
    },
    {
      id: 'JOB010',
      title: 'Research Scientist',
      company: 'MIT Lincoln Laboratory',
      logo: 'https://cdn-icons-png.flaticon.com/512/2103/2103798.png',
      location: 'Boston, USA',
      field: 'research',
      type: 'full-time',
      experience: 'senior',
      salary: '$110,000-$140,000',
      applicationDeadline: '2024-04-30',
      postedDate: '2024-02-06',
      description: 'Conduct cutting-edge research in advanced technologies.',
      requirements: [
        'PhD in Engineering or Physics',
        '5+ years of research experience',
        'Strong publication record',
        'Experience with advanced instrumentation',
        'Security clearance eligible'
      ],
      responsibilities: [
        'Conduct experimental research',
        'Publish findings in journals',
        'Secure research funding',
        'Mentor graduate students',
        'Present at conferences'
      ],
      benefits: [
        'Research autonomy',
        'Conference travel budget',
        'Academic collaborations',
        'State-of-the-art facilities',
        'Academic appointment option'
      ],
      applyLink: 'https://www.ll.mit.edu/careers',
      contactEmail: 'research-jobs@ll.mit.edu',
      spots: 1,
      applications: 95,
      featured: true,
      urgent: false,
    },
  ]);

  useEffect(() => {
    loadInitialData();
    setupNetInfoListener();
  }, []);

  useEffect(() => {
    filterJobs();
  }, [searchQuery, selectedField, selectedLocation, selectedExperience, selectedType, jobs]);

  const setupNetInfoListener = () => {
    const unsubscribe = NetInfo.addEventListener(state => {
      setIsOnline(state.isConnected);
    });
    return unsubscribe;
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadDarkModePreference(),
        loadSavedJobs(),
        loadAppliedJobs(),
      ]);
      filterJobs();
    } catch (error) {
      console.error('Error loading initial data:', error);
      setError('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const loadDarkModePreference = async () => {
    try {
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
    } catch (error) {
      console.error('Error loading dark mode preference:', error);
    }
  };

  const loadSavedJobs = async () => {
    try {
      const saved = await AsyncStorage.getItem('savedJobs');
      if (saved) {
        setSavedJobs(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading saved jobs:', error);
    }
  };

  const loadAppliedJobs = async () => {
    try {
      const applied = await AsyncStorage.getItem('appliedJobs');
      if (applied) {
        setAppliedJobs(JSON.parse(applied));
      }
    } catch (error) {
      console.error('Error loading applied jobs:', error);
    }
  };

  const refreshData = async () => {
    if (!isOnline) return;
    
    setIsRefreshing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      filterJobs();
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const filterJobs = () => {
    let filtered = [...jobs];
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(job =>
        String(job.title ?? '').toLowerCase().includes(query) ||
        String(job.company ?? '').toLowerCase().includes(query) ||
        String(job.description ?? '').toLowerCase().includes(query) ||
        String(job.location ?? '').toLowerCase().includes(query)
      );
    }
    
    if (selectedField !== 'all') {
      filtered = filtered.filter(job => job.field === selectedField);
    }
    
    if (selectedLocation !== 'all') {
      filtered = filtered.filter(job => {
        if (selectedLocation === 'remote') {
          return String(job.location ?? '').toLowerCase().includes('remote');
        } else if (selectedLocation === 'hybrid') {
          return String(job.location ?? '').toLowerCase().includes('hybrid');
        } else if (selectedLocation === 'uk') {
          return String(job.location ?? '').toLowerCase().includes('uk') || 
                 String(job.location ?? '').toLowerCase().includes('london') ||
                 String(job.location ?? '').toLowerCase().includes('manchester');
        }
        return String(job.location ?? '').toLowerCase().includes(selectedLocation.toLowerCase());
      });
    }
    
    if (selectedExperience !== 'all') {
      filtered = filtered.filter(job => job.experience === selectedExperience);
    }
    
    if (selectedType !== 'all') {
      filtered = filtered.filter(job => job.type === selectedType);
    }
    
    setFilteredJobs(filtered);
  };

  const handleSearch = (text) => {
    setSearchQuery(text);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      filterJobs();
    }, 300);
  };

  const toggleSaveJob = async (jobId) => {
    try {
      let newSaved;
      if (savedJobs.includes(jobId)) {
        newSaved = savedJobs.filter(id => id !== jobId);
      } else {
        newSaved = [...savedJobs, jobId];
      }
      
      setSavedJobs(newSaved);
      await AsyncStorage.setItem('savedJobs', JSON.stringify(newSaved));
    } catch (error) {
      console.error('Error toggling save:', error);
    }
  };

  const applyToJob = async (jobId) => {
    try {
      const newApplied = [...appliedJobs, jobId];
      setAppliedJobs(newApplied);
      await AsyncStorage.setItem('appliedJobs', JSON.stringify(newApplied));
      
      Alert.alert(
        'Application Submitted',
        'Your application has been submitted successfully! You\'ll hear back from the company soon.',
        [{ text: 'OK' }]
      );
    } catch (error) {
      console.error('Error applying to job:', error);
    }
  };

  const navigateToApply = (job) => {
    navigation.navigate('JobApply', { 
      job,
      onApplySuccess: () => {
        applyToJob(job.id);
      }
    });
  };

  const openApplicationLink = (link) => {
    Linking.openURL(link).catch(err => {
      console.error('Failed to open link:', err);
      Alert.alert('Error', 'Could not open application link');
    });
  };

  const shareJob = async (job) => {
    try {
      await Share.share({
        title: job.title,
        message: `Check out this job: ${job.title} at ${job.company}\n\nLocation: ${job.location}\nSalary: ${job.salary}\n\nApply here: ${job.applyLink}`,
        url: job.applyLink,
      });
    } catch (error) {
      console.error('Error sharing job:', error);
    }
  };

  const calculateDaysLeft = (deadline) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const calculateDaysPosted = (postedDate) => {
    const today = new Date();
    const posted = new Date(postedDate);
    const diffTime = today - posted;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getFieldColor = (field) => {
    const colors = darkMode ? {
      software: ['#4c51bf', '#667eea'],
      mechanical: ['#dd6b20', '#ed8936'],
      civil: ['#2b6cb0', '#4299e1'],
      electrical: ['#d69e2e', '#ecc94b'],
      chemical: ['#38a169', '#68d391'],
      aerospace: ['#9f7aea', '#b794f4'],
      biomedical: ['#ed64a6', '#f687b3'],
      data: ['#4c51bf', '#667eea'],
      management: ['#805ad5', '#9f7aea'],
      research: ['#319795', '#38b2ac'],
      default: ['#718096', '#a0aec0'],
    } : {
      software: ['#6366f1', '#818cf8'],
      mechanical: ['#f97316', '#fb923c'],
      civil: ['#3b82f6', '#60a5fa'],
      electrical: ['#eab308', '#facc15'],
      chemical: ['#10b981', '#34d399'],
      aerospace: ['#8b5cf6', '#a78bfa'],
      biomedical: ['#ec4899', '#f472b6'],
      data: ['#6366f1', '#818cf8'],
      management: ['#7c3aed', '#8b5cf6'],
      research: ['#0d9488', '#14b8a6'],
      default: ['#6b7280', '#9ca3af'],
    };
    
    return colors[field] || colors.default;
  };

  const getFieldIcon = (field) => {
    const icons = {
      software: 'code-braces',
      mechanical: 'cog',
      civil: 'bridge',
      electrical: 'flash',
      chemical: 'flask',
      aerospace: 'airplane',
      biomedical: 'heart-pulse',
      data: 'chart-bar',
      management: 'briefcase',
      research: 'microscope',
      default: 'briefcase',
    };
    
    return icons[field] || icons.default;
  };

  const getExperienceColor = (experience) => {
    const colors = {
      entry: '#10b981',
      mid: '#3b82f6',
      senior: '#8b5cf6',
      lead: '#f59e0b',
      default: '#6b7280',
    };
    return colors[experience] || colors.default;
  };

  const renderHeader = () => (
    <LinearGradient
      colors={darkMode ? ['#2d3748', '#4a5568'] : ['#667eea', '#764ba2']}
      style={styles.header}
    >
      <TouchableOpacity 
        style={styles.backButton}
        onPress={() => navigation.goBack()}
      >
        <MaterialCommunityIcons name="arrow-left" size={22} color="#ffffff" />
      </TouchableOpacity>
      <View style={styles.headerCenter}>
        <Text style={styles.headerTitle}>💼 Engineering Jobs</Text>
        <Text style={styles.headerSubtitle}>
          {filteredJobs.length} opportunities
        </Text>
      </View>
      <TouchableOpacity 
        style={styles.viewModeButton}
        onPress={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
      >
        <MaterialCommunityIcons 
          name={viewMode === 'grid' ? 'view-list' : 'view-grid'} 
          size={22} 
          color="#ffffff" 
        />
      </TouchableOpacity>
    </LinearGradient>
  );

  const renderSearchBar = () => (
    <View style={styles.searchContainer}>
      <MaterialCommunityIcons 
        name="magnify" 
        size={18} 
        color={darkMode ? "#a0aec0" : "#6b7280"} 
        style={styles.searchIcon}
      />
      <TextInput
        style={styles.searchInput}
        placeholder="Search jobs, companies, locations..."
        value={searchQuery}
        onChangeText={handleSearch}
        placeholderTextColor={darkMode ? "#a0aec0" : "#6b7280"}
      />
      {searchQuery.length > 0 && (
        <TouchableOpacity onPress={() => setSearchQuery('')}>
          <MaterialCommunityIcons 
            name="close-circle" 
            size={18} 
            color={darkMode ? "#a0aec0" : "#6b7280"} 
          />
        </TouchableOpacity>
      )}
    </View>
  );

  const renderFilterChips = () => (
    <View style={styles.filterContainer}>
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.filterScrollContent}
      >
        <TouchableOpacity
          style={styles.filterButton}
          onPress={() => setShowFilters(!showFilters)}
        >
          <MaterialCommunityIcons 
            name="filter-variant" 
            size={14} 
            color="#ffffff" 
          />
          <Text style={styles.filterButtonText}>Filters</Text>
        </TouchableOpacity>

        {selectedField !== 'all' && (
          <TouchableOpacity
            style={styles.activeFilterChip}
            onPress={() => setSelectedField('all')}
          >
            <Text style={styles.activeFilterText}>{selectedField}</Text>
            <MaterialCommunityIcons name="close" size={12} color="#ffffff" />
          </TouchableOpacity>
        )}

        {selectedLocation !== 'all' && (
          <TouchableOpacity
            style={styles.activeFilterChip}
            onPress={() => setSelectedLocation('all')}
          >
            <Text style={styles.activeFilterText}>{selectedLocation}</Text>
            <MaterialCommunityIcons name="close" size={12} color="#ffffff" />
          </TouchableOpacity>
        )}

        {selectedExperience !== 'all' && (
          <TouchableOpacity
            style={styles.activeFilterChip}
            onPress={() => setSelectedExperience('all')}
          >
            <Text style={styles.activeFilterText}>{selectedExperience}</Text>
            <MaterialCommunityIcons name="close" size={12} color="#ffffff" />
          </TouchableOpacity>
        )}
      </ScrollView>
    </View>
  );

  const renderFiltersModal = () => (
    <Modal
      visible={showFilters}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowFilters(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={[styles.modalContent, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <View style={styles.modalHeader}>
            <View style={styles.modalTitleContainer}>
              <MaterialCommunityIcons name="filter-variant" size={18} color={darkMode ? '#ffffff' : '#1f2937'} />
              <Text style={[styles.modalTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                Filter Jobs
              </Text>
            </View>
            <TouchableOpacity onPress={() => setShowFilters(false)}>
              <MaterialCommunityIcons name="close" size={20} color={darkMode ? '#a0aec0' : '#6b7280'} />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.filterOptions}>
            <View style={styles.filterSection}>
              <Text style={[styles.filterSectionTitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Engineering Field
              </Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.categoryOptions}>
                  {fields.map((field) => (
                    <TouchableOpacity
                      key={field.id}
                      style={[
                        styles.categoryChip,
                        selectedField === field.id && styles.categoryChipActive
                      ]}
                      onPress={() => setSelectedField(field.id)}
                    >
                      <MaterialCommunityIcons 
                        name={field.icon} 
                        size={14} 
                        color={selectedField === field.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
                      />
                      <Text style={[
                        styles.categoryChipText,
                        selectedField === field.id && styles.categoryChipTextActive
                      ]}>
                        {field.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            <View style={styles.filterSection}>
              <Text style={[styles.filterSectionTitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Location
              </Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.locationOptions}>
                  {locations.map((location) => (
                    <TouchableOpacity
                      key={location.id}
                      style={[
                        styles.locationChip,
                        selectedLocation === location.id && styles.locationChipActive
                      ]}
                      onPress={() => setSelectedLocation(location.id)}
                    >
                      <Text style={[
                        styles.locationChipText,
                        selectedLocation === location.id && styles.locationChipTextActive
                      ]}>
                        {location.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            <View style={styles.filterSection}>
              <Text style={[styles.filterSectionTitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Experience Level
              </Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.experienceOptions}>
                  {experiences.map((experience) => (
                    <TouchableOpacity
                      key={experience.id}
                      style={[
                        styles.experienceChip,
                        selectedExperience === experience.id && styles.experienceChipActive
                      ]}
                      onPress={() => setSelectedExperience(experience.id)}
                    >
                      <Text style={[
                        styles.experienceChipText,
                        selectedExperience === experience.id && styles.experienceChipTextActive
                      ]}>
                        {experience.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            <View style={styles.filterSection}>
              <Text style={[styles.filterSectionTitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Job Type
              </Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.typeOptions}>
                  {types.map((type) => (
                    <TouchableOpacity
                      key={type.id}
                      style={[
                        styles.typeChip,
                        selectedType === type.id && styles.typeChipActive
                      ]}
                      onPress={() => setSelectedType(type.id)}
                    >
                      <Text style={[
                        styles.typeChipText,
                        selectedType === type.id && styles.typeChipTextActive
                      ]}>
                        {type.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>
          </ScrollView>

          <View style={styles.filterActions}>
            <TouchableOpacity 
              style={styles.resetButton}
              onPress={() => {
                setSelectedField('all');
                setSelectedLocation('all');
                setSelectedExperience('all');
                setSelectedType('all');
              }}
            >
              <Text style={styles.resetButtonText}>Reset All</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={styles.applyButton}
              onPress={() => setShowFilters(false)}
            >
              <Text style={styles.applyButtonText}>Apply</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  const renderJobGrid = ({ item, index }) => {
    const isSaved = savedJobs.includes(item.id);
    const isApplied = appliedJobs.includes(item.id);
    const daysLeft = calculateDaysLeft(item.applicationDeadline);
    const daysPosted = calculateDaysPosted(item.postedDate);
    
    return (
      <TouchableOpacity
        style={styles.jobCardGrid}
        onPress={() => {
          setSelectedJob(item);
          setDetailModalVisible(true);
        }}
        activeOpacity={0.8}
      >
        <View style={styles.jobHeaderGrid}>
          <LinearGradient
            colors={getFieldColor(item.field)}
            style={styles.jobFieldGrid}
          >
            <MaterialCommunityIcons name={getFieldIcon(item.field)} size={16} color="#ffffff" />
            <Text style={styles.jobFieldText}>{String(item.field ?? '').toUpperCase()}</Text>
          </LinearGradient>
          
          <TouchableOpacity 
            style={styles.saveButtonGrid}
            onPress={() => toggleSaveJob(item.id)}
          >
            <MaterialCommunityIcons 
              name={isSaved ? "bookmark" : "bookmark-outline"} 
              size={18} 
              color={isSaved ? "#667eea" : (darkMode ? "#a0aec0" : "#6b7280")} 
            />
          </TouchableOpacity>
        </View>

        <View style={styles.jobContentGrid}>
          <Text style={styles.jobTitleGrid} numberOfLines={2}>
            {item.title}
          </Text>
          
          <View style={styles.companyContainer}>
            <View style={styles.companyLogo}>
              <MaterialCommunityIcons name="office-building" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
            </View>
            <Text style={styles.companyText} numberOfLines={1}>
              {item.company}
            </Text>
          </View>

          <View style={styles.jobMetaGrid}>
            <View style={styles.metaItem}>
              <MaterialCommunityIcons name="map-marker" size={10} color={darkMode ? "#a0aec0" : "#6b7280"} />
              <Text style={styles.metaText}>{item.location}</Text>
            </View>
            <View style={styles.metaItem}>
              <MaterialCommunityIcons name="briefcase" size={10} color={darkMode ? "#a0aec0" : "#6b7280"} />
              <Text style={styles.metaText}>{item.type}</Text>
            </View>
          </View>

          <View style={styles.jobStatsGrid}>
            <View style={styles.statItem}>
              <MaterialCommunityIcons name="currency-usd" size={10} color="#10b981" />
              <Text style={styles.statText}>{item.salary}</Text>
            </View>
            <View style={[styles.statItem, { backgroundColor: getExperienceColor(item.experience) + '20' }]}>
              <Text style={[styles.statText, { color: getExperienceColor(item.experience) }]}>
                {item.experience}
              </Text>
            </View>
          </View>

          {item.featured && (
            <View style={styles.featuredBadge}>
              <MaterialCommunityIcons name="star" size={10} color="#ffffff" />
              <Text style={styles.featuredText}>Featured</Text>
            </View>
          )}

          {item.urgent && (
            <View style={styles.urgentBadge}>
              <MaterialCommunityIcons name="alert" size={10} color="#ffffff" />
              <Text style={styles.urgentText}>Urgent</Text>
            </View>
          )}

          <View style={styles.timeInfo}>
            <View style={styles.timeItem}>
              <MaterialCommunityIcons name="clock-outline" size={8} color="#6b7280" />
              <Text style={styles.timeText}>{daysPosted}d ago</Text>
            </View>
            <View style={styles.timeItem}>
              <MaterialCommunityIcons name="calendar-clock" size={8} color="#ef4444" />
              <Text style={styles.timeText}>{daysLeft}d left</Text>
            </View>
          </View>
        </View>

        <View style={styles.jobActionsGrid}>
          {isApplied ? (
            <TouchableOpacity 
              style={styles.appliedButton}
              disabled={true}
            >
              <MaterialCommunityIcons name="check-circle" size={14} color="#ffffff" />
              <Text style={styles.appliedText}>Applied</Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity 
              style={styles.applyButtonSmall}
              onPress={() => navigateToApply(item)}
            >
              <Text style={styles.applyTextSmall}>Apply Now</Text>
              <MaterialCommunityIcons name="arrow-right" size={12} color="#ffffff" />
            </TouchableOpacity>
          )}
          
          <TouchableOpacity 
            style={styles.detailsButton}
            onPress={() => {
              setSelectedJob(item);
              setDetailModalVisible(true);
            }}
          >
            <Text style={styles.detailsText}>Details</Text>
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    );
  };

  const renderJobList = ({ item, index }) => {
    const isSaved = savedJobs.includes(item.id);
    const isApplied = appliedJobs.includes(item.id);
    const daysLeft = calculateDaysLeft(item.applicationDeadline);
    const daysPosted = calculateDaysPosted(item.postedDate);
    
    return (
      <TouchableOpacity
        style={styles.jobCardList}
        onPress={() => {
          setSelectedJob(item);
          setDetailModalVisible(true);
        }}
        activeOpacity={0.8}
      >
        <LinearGradient
          colors={getFieldColor(item.field)}
          style={styles.jobFieldList}
        >
          <MaterialCommunityIcons name={getFieldIcon(item.field)} size={18} color="#ffffff" />
        </LinearGradient>

        <View style={styles.jobContentList}>
          <View style={styles.jobHeaderList}>
            <View style={styles.titleContainer}>
              <Text style={styles.jobTitleList} numberOfLines={1}>
                {item.title}
              </Text>
              <Text style={styles.companyTextList}>{item.company}</Text>
            </View>
            <TouchableOpacity 
              style={styles.saveButtonList}
              onPress={() => toggleSaveJob(item.id)}
            >
              <MaterialCommunityIcons 
                name={isSaved ? "bookmark" : "bookmark-outline"} 
                size={18} 
                color={isSaved ? "#667eea" : (darkMode ? "#a0aec0" : "#6b7280")} 
              />
            </TouchableOpacity>
          </View>

          <View style={styles.jobDetailsList}>
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="map-marker" size={12} color={darkMode ? "#a0aec0" : "#6b7280"} />
              <Text style={styles.detailText}>{item.location}</Text>
            </View>
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="briefcase" size={12} color={darkMode ? "#a0aec0" : "#6b7280"} />
              <Text style={styles.detailText}>{item.type}</Text>
            </View>
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="currency-usd" size={12} color="#10b981" />
              <Text style={styles.detailText}>{item.salary}</Text>
            </View>
          </View>

          <View style={styles.jobFooterList}>
            <View style={styles.experienceContainer}>
              <View style={[styles.experienceBadge, { backgroundColor: getExperienceColor(item.experience) + '20' }]}>
                <Text style={[styles.experienceText, { color: getExperienceColor(item.experience) }]}>
                  {item.experience}
                </Text>
              </View>
              <View style={styles.timeInfoList}>
                <MaterialCommunityIcons name="clock-outline" size={10} color="#6b7280" />
                <Text style={styles.timeTextList}>{daysPosted}d ago • {daysLeft}d left</Text>
              </View>
            </View>
            
            <View style={styles.jobActionsList}>
              {isApplied ? (
                <TouchableOpacity 
                  style={styles.appliedButtonList}
                  disabled={true}
                >
                  <MaterialCommunityIcons name="check-circle" size={12} color="#ffffff" />
                  <Text style={styles.appliedTextList}>Applied</Text>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity 
                  style={styles.applyButtonList}
                  onPress={() => navigateToApply(item)}
                >
                  <Text style={styles.applyTextList}>Quick Apply</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const renderDetailModal = () => (
    <Modal
      visible={detailModalVisible}
      animationType="slide"
      onRequestClose={() => setDetailModalVisible(false)}
    >
      <SafeAreaView style={[styles.detailModalContainer, { backgroundColor: darkMode ? '#1a202c' : '#ffffff' }]}>
        {selectedJob && (
          <>
            <LinearGradient
              colors={getFieldColor(selectedJob.field)}
              style={styles.detailHeader}
            >
              <TouchableOpacity 
                style={styles.detailBackButton}
                onPress={() => setDetailModalVisible(false)}
              >
                <MaterialCommunityIcons name="close" size={22} color="#ffffff" />
              </TouchableOpacity>
              <View style={styles.detailHeaderCenter}>
                <Text style={styles.detailTitle} numberOfLines={2}>
                  {selectedJob.title}
                </Text>
                <Text style={styles.detailSubtitle}>
                  {selectedJob.company} • {selectedJob.location}
                </Text>
              </View>
              <TouchableOpacity 
                style={styles.detailShareButton}
                onPress={() => shareJob(selectedJob)}
              >
                <MaterialCommunityIcons name="share-variant" size={22} color="#ffffff" />
              </TouchableOpacity>
            </LinearGradient>

            <ScrollView style={styles.detailContent}>
              <View style={styles.detailOverview}>
                <View style={styles.overviewItem}>
                  <MaterialCommunityIcons name="briefcase" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={[styles.overviewLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Type
                  </Text>
                  <Text style={[styles.overviewValue, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                    {selectedJob.type}
                  </Text>
                </View>
                
                <View style={styles.overviewItem}>
                  <MaterialCommunityIcons name="chart-line" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={[styles.overviewLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Experience
                  </Text>
                  <Text style={[styles.overviewValue, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                    {selectedJob.experience}
                  </Text>
                </View>
                
                <View style={styles.overviewItem}>
                  <MaterialCommunityIcons name="currency-usd" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={[styles.overviewLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Salary
                  </Text>
                  <Text style={[styles.overviewValue, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                    {selectedJob.salary}
                  </Text>
                </View>
                
                <View style={styles.overviewItem}>
                  <MaterialCommunityIcons name="clock-outline" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={[styles.overviewLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Deadline
                  </Text>
                  <Text style={[styles.overviewValue, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                    {selectedJob.applicationDeadline}
                  </Text>
                </View>
                
                <View style={styles.overviewItem}>
                  <MaterialCommunityIcons name="calendar" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={[styles.overviewLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Posted
                  </Text>
                  <Text style={[styles.overviewValue, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                    {calculateDaysPosted(selectedJob.postedDate)} days ago
                  </Text>
                </View>
                
                <View style={styles.overviewItem}>
                  <MaterialCommunityIcons name="account-group" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={[styles.overviewLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Openings
                  </Text>
                  <Text style={[styles.overviewValue, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                    {selectedJob.spots} available
                  </Text>
                </View>
              </View>

              <View style={styles.detailSection}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  Job Description
                </Text>
                <Text style={[styles.sectionContent, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                  {selectedJob.description}
                </Text>
              </View>

              <View style={styles.detailSection}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  Requirements
                </Text>
                {selectedJob.requirements.map((req, index) => (
                  <View key={index} style={styles.listItem}>
                    <MaterialCommunityIcons name="check-circle" size={14} color="#10b981" />
                    <Text style={[styles.listText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                      {req}
                    </Text>
                  </View>
                ))}
              </View>

              <View style={styles.detailSection}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  Responsibilities
                </Text>
                {selectedJob.responsibilities.map((resp, index) => (
                  <View key={index} style={styles.listItem}>
                    <MaterialCommunityIcons name="arrow-right-circle" size={14} color="#667eea" />
                    <Text style={[styles.listText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                      {resp}
                    </Text>
                  </View>
                ))}
              </View>

              <View style={styles.detailSection}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  Benefits & Perks
                </Text>
                {selectedJob.benefits.map((benefit, index) => (
                  <View key={index} style={styles.listItem}>
                    <MaterialCommunityIcons name="gift" size={14} color="#f59e0b" />
                    <Text style={[styles.listText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                      {benefit}
                    </Text>
                  </View>
                ))}
              </View>

              <View style={styles.detailSection}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  Application Statistics
                </Text>
                <View style={styles.statsContainer}>
                  <View style={styles.statItemDetail}>
                    <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                      {selectedJob.applications}
                    </Text>
                    <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                      Total Applications
                    </Text>
                  </View>
                  <View style={styles.statItemDetail}>
                    <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                      {selectedJob.spots}
                    </Text>
                    <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                      Available Positions
                    </Text>
                  </View>
                  <View style={styles.statItemDetail}>
                    <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                      {Math.round((selectedJob.applications / selectedJob.spots) * 100)}:1
                    </Text>
                    <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                      Competition Ratio
                    </Text>
                  </View>
                </View>
              </View>
            </ScrollView>

            <View style={[styles.detailFooter, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
              <TouchableOpacity 
                style={styles.saveDetailButton}
                onPress={() => toggleSaveJob(selectedJob.id)}
              >
                <MaterialCommunityIcons 
                  name={savedJobs.includes(selectedJob.id) ? "bookmark" : "bookmark-outline"} 
                  size={20} 
                  color={darkMode ? "#a0aec0" : "#6b7280"} 
                />
                <Text style={[styles.saveDetailText, { color: darkMode ? "#a0aec0" : "#6b7280" }]}>
                  {savedJobs.includes(selectedJob.id) ? 'Saved' : 'Save'}
                </Text>
              </TouchableOpacity>
              
              {appliedJobs.includes(selectedJob.id) ? (
                <TouchableOpacity 
                  style={styles.appliedDetailButton}
                  disabled={true}
                >
                  <MaterialCommunityIcons name="check-circle" size={20} color="#ffffff" />
                  <Text style={styles.appliedDetailText}>Already Applied</Text>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity 
                  style={styles.applyDetailButton}
                  onPress={() => {
                    setDetailModalVisible(false);
                    navigateToApply(selectedJob);
                  }}
                >
                  <Text style={styles.applyDetailText}>Apply Now</Text>
                  <MaterialCommunityIcons name="arrow-right" size={18} color="#ffffff" />
                </TouchableOpacity>
              )}
            </View>
          </>
        )}
      </SafeAreaView>
    </Modal>
  );

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <MaterialCommunityIcons 
        name="briefcase-search" 
        size={48} 
        color={darkMode ? "#4a5568" : "#d1d5db"} 
      />
      <Text style={[styles.emptyStateTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
        No Jobs Found
      </Text>
      <Text style={[styles.emptyStateText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
        Try adjusting your filters or check back later for new opportunities
      </Text>
      <TouchableOpacity 
        style={styles.resetFiltersButton}
        onPress={() => {
          setSearchQuery('');
          setSelectedField('all');
          setSelectedLocation('all');
          setSelectedExperience('all');
          setSelectedType('all');
        }}
      >
        <Text style={styles.resetFiltersText}>Reset All Filters</Text>
      </TouchableOpacity>
    </View>
  );

  const renderHeaderComponent = () => (
    <>
      {!isOnline && (
        <View style={styles.offlineBanner}>
          <MaterialCommunityIcons name="wifi-off" size={14} color="#ffffff" />
          <Text style={styles.offlineText}>Offline Mode - Showing cached data</Text>
        </View>
      )}

      <View style={styles.statsOverview}>
        <View style={[styles.statCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <MaterialCommunityIcons name="briefcase" size={20} color="#667eea" />
          <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
            {jobs.length}
          </Text>
          <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Total Jobs
          </Text>
        </View>
        
        <View style={[styles.statCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <MaterialCommunityIcons name="check-circle" size={20} color="#10b981" />
          <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
            {appliedJobs.length}
          </Text>
          <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Applied
          </Text>
        </View>
        
        <View style={[styles.statCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <MaterialCommunityIcons name="bookmark" size={20} color="#f59e0b" />
          <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
            {savedJobs.length}
          </Text>
          <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Saved
          </Text>
        </View>
        
        <View style={[styles.statCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <MaterialCommunityIcons name="star" size={20} color="#8b5cf6" />
          <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
            {jobs.filter(j => j.featured).length}
          </Text>
          <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Featured
          </Text>
        </View>
      </View>

      <View style={styles.contentContainer}>
        <View style={styles.sectionHeader}>
          <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
            Job Opportunities ({filteredJobs.length})
          </Text>
          <TouchableOpacity onPress={() => setShowFilters(true)}>
            <Text style={styles.filterLink}>Filter & Sort</Text>
          </TouchableOpacity>
        </View>
      </View>
    </>
  );

  if (loading) {
    return (
      <SafeAreaView style={[styles.loadingContainer, { backgroundColor: darkMode ? '#1a202c' : '#f0f4f8' }]}>
        <ActivityIndicator size="large" color="#667eea" />
        <Text style={[styles.loadingText, { color: darkMode ? '#cbd5e0' : '#6b7280' }]}>
          Loading jobs...
        </Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: darkMode ? '#1a202c' : '#f0f4f8' }]}>
      <StatusBar barStyle={darkMode ? "light-content" : "light-content"} backgroundColor="#667eea" />
      
      {renderHeader()}
      {renderSearchBar()}
      {renderFilterChips()}
      
      <FlatList
        ref={flatListRef}
        data={filteredJobs}
        key={viewMode === 'grid' ? 'grid' : 'list'}
        keyExtractor={(item) => item.id}
        numColumns={viewMode === 'grid' ? 2 : 1}
        contentContainerStyle={styles.scrollContent}
        ListHeaderComponent={renderHeaderComponent}
        ListEmptyComponent={renderEmptyState}
        renderItem={viewMode === 'grid' ? renderJobGrid : renderJobList}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={refreshData}
            colors={['#667eea']}
            tintColor="#667eea"
          />
        }
        showsVerticalScrollIndicator={false}
        style={styles.scrollView}
      />

      {renderFiltersModal()}
      {renderDetailModal()}

      <TouchableOpacity 
        style={styles.fab}
        onPress={() => navigation.navigate('SavedJobs', { savedJobs })}
      >
        <LinearGradient
          colors={['#667eea', '#764ba2']}
          style={styles.fabGradient}
        >
          <MaterialCommunityIcons name="bookmark" size={20} color="#ffffff" />
          {savedJobs.length > 0 && (
            <View style={styles.fabBadge}>
              <Text style={styles.fabBadgeText}>{savedJobs.length}</Text>
            </View>
          )}
        </LinearGradient>
      </TouchableOpacity>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  backButton: {
    padding: 6,
  },
  headerCenter: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  headerSubtitle: {
    fontSize: 11,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  viewModeButton: {
    padding: 6,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    marginHorizontal: 16,
    marginVertical: 8,
    borderRadius: 10,
    paddingHorizontal: 12,
    height: 40,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    height: 40,
    fontSize: 14,
    color: '#ffffff',
  },
  filterContainer: {
    height: 32,
    marginBottom: 4,
  },
  filterScrollContent: {
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#667eea',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 6,
    height: 24,
  },
  filterButtonText: {
    color: '#ffffff',
    fontSize: 11,
    fontWeight: '500',
    marginLeft: 4,
  },
  activeFilterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#8b5cf6',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 6,
    height: 24,
  },
  activeFilterText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: '500',
    marginRight: 3,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 16,
  },
  offlineBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#6b7280',
    paddingVertical: 6,
    marginHorizontal: 16,
    borderRadius: 6,
    marginTop: 4,
    marginBottom: 8,
  },
  offlineText: {
    color: '#ffffff',
    fontSize: 11,
    marginLeft: 6,
  },
  statsOverview: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: 16,
    marginBottom: 12,
    gap: 8,
  },
  statCard: {
    flex: 1,
    minWidth: '45%',
    alignItems: 'center',
    padding: 10,
    borderRadius: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  statNumber: {
    fontSize: 16,
    fontWeight: 'bold',
    marginVertical: 4,
  },
  statLabel: {
    fontSize: 10,
  },
  contentContainer: {
    paddingHorizontal: 16,
    marginBottom: 8,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  filterLink: {
    fontSize: 12,
    color: '#667eea',
    fontWeight: '600',
  },
  jobCardGrid: {
    flex: 1,
    margin: 6,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
    minHeight: 200,
  },
  jobHeaderGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingTop: 10,
  },
  jobFieldGrid: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  jobFieldText: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#ffffff',
    marginLeft: 4,
  },
  saveButtonGrid: {
    padding: 2,
  },
  jobContentGrid: {
    padding: 10,
    flex: 1,
  },
  jobTitleGrid: {
    fontSize: 12,
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: 6,
    lineHeight: 16,
  },
  companyContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  companyLogo: {
    width: 20,
    height: 20,
    borderRadius: 4,
    backgroundColor: '#f3f4f6',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 6,
  },
  companyText: {
    fontSize: 10,
    color: '#6b7280',
    flex: 1,
  },
  jobMetaGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  metaText: {
    fontSize: 9,
    color: '#6b7280',
    marginLeft: 3,
  },
  jobStatsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 4,
    marginBottom: 8,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  statText: {
    fontSize: 9,
    fontWeight: '600',
    marginLeft: 3,
  },
  featuredBadge: {
    position: 'absolute',
    top: 10,
    right: 10,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f59e0b',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
  },
  featuredText: {
    fontSize: 8,
    color: '#ffffff',
    fontWeight: 'bold',
    marginLeft: 2,
  },
  urgentBadge: {
    position: 'absolute',
    top: 30,
    right: 10,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ef4444',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
  },
  urgentText: {
    fontSize: 8,
    color: '#ffffff',
    fontWeight: 'bold',
    marginLeft: 2,
  },
  timeInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 4,
  },
  timeItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  timeText: {
    fontSize: 8,
    color: '#6b7280',
    marginLeft: 3,
  },
  jobActionsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  appliedButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#10b981',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    flex: 1,
    justifyContent: 'center',
  },
  appliedText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: '600',
    marginLeft: 4,
  },
  applyButtonSmall: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#667eea',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    flex: 1,
    justifyContent: 'center',
  },
  applyTextSmall: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: '600',
    marginRight: 4,
  },
  detailsButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    marginLeft: 8,
  },
  detailsText: {
    fontSize: 10,
    color: '#6b7280',
    fontWeight: '500',
  },
  jobCardList: {
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    borderRadius: 12,
    marginBottom: 8,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
    marginHorizontal: 16,
    minHeight: 110,
  },
  jobFieldList: {
    width: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  jobContentList: {
    flex: 1,
    padding: 12,
  },
  jobHeaderList: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  titleContainer: {
    flex: 1,
    marginRight: 8,
  },
  jobTitleList: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 4,
  },
  companyTextList: {
    fontSize: 11,
    color: '#6b7280',
  },
  saveButtonList: {
    padding: 2,
  },
  jobDetailsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 8,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  detailText: {
    fontSize: 11,
    color: '#6b7280',
    marginLeft: 4,
  },
  jobFooterList: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  experienceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  experienceBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  experienceText: {
    fontSize: 9,
    fontWeight: '600',
  },
  timeInfoList: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  timeTextList: {
    fontSize: 10,
    color: '#6b7280',
    marginLeft: 3,
  },
  jobActionsList: {
    flexDirection: 'row',
    gap: 8,
  },
  appliedButtonList: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#10b981',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
  },
  appliedTextList: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: '600',
    marginLeft: 4,
  },
  applyButtonList: {
    backgroundColor: '#667eea',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  applyTextList: {
    color: '#ffffff',
    fontSize: 11,
    fontWeight: '600',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
    paddingHorizontal: 16,
  },
  emptyStateTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginTop: 12,
  },
  emptyStateText: {
    fontSize: 13,
    marginTop: 6,
    textAlign: 'center',
  },
  resetFiltersButton: {
    backgroundColor: '#667eea',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 6,
    marginTop: 12,
  },
  resetFiltersText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '70%',
    paddingBottom: Platform.OS === 'ios' ? 34 : 16,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  modalTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  filterOptions: {
    paddingHorizontal: 14,
    paddingTop: 10,
  },
  filterSection: {
    marginBottom: 16,
  },
  filterSectionTitle: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 8,
  },
  categoryOptions: {
    flexDirection: 'row',
    gap: 6,
    paddingVertical: 2,
  },
  categoryChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 14,
  },
  categoryChipActive: {
    backgroundColor: '#667eea',
  },
  categoryChipText: {
    fontSize: 11,
    color: '#6b7280',
    marginLeft: 4,
  },
  categoryChipTextActive: {
    color: '#ffffff',
  },
  locationOptions: {
    flexDirection: 'row',
    gap: 6,
    paddingVertical: 2,
  },
  locationChip: {
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 14,
  },
  locationChipActive: {
    backgroundColor: '#667eea',
  },
  locationChipText: {
    fontSize: 11,
    color: '#6b7280',
  },
  locationChipTextActive: {
    color: '#ffffff',
  },
  experienceOptions: {
    flexDirection: 'row',
    gap: 6,
    paddingVertical: 2,
  },
  experienceChip: {
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 14,
  },
  experienceChipActive: {
    backgroundColor: '#667eea',
  },
  experienceChipText: {
    fontSize: 11,
    color: '#6b7280',
  },
  experienceChipTextActive: {
    color: '#ffffff',
  },
  typeOptions: {
    flexDirection: 'row',
    gap: 6,
    paddingVertical: 2,
  },
  typeChip: {
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 14,
  },
  typeChipActive: {
    backgroundColor: '#667eea',
  },
  typeChipText: {
    fontSize: 11,
    color: '#6b7280',
  },
  typeChipTextActive: {
    color: '#ffffff',
  },
  filterActions: {
    flexDirection: 'row',
    paddingHorizontal: 14,
    paddingTop: 12,
    gap: 8,
  },
  resetButton: {
    flex: 1,
    backgroundColor: '#e5e7eb',
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: 'center',
  },
  applyButton: {
    flex: 1,
    backgroundColor: '#667eea',
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: 'center',
  },
  resetButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#6b7280',
  },
  applyButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#ffffff',
  },
  detailModalContainer: {
    flex: 1,
  },
  detailHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  detailBackButton: {
    padding: 6,
  },
  detailHeaderCenter: {
    flex: 1,
    marginHorizontal: 10,
  },
  detailTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  detailSubtitle: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 4,
  },
  detailShareButton: {
    padding: 6,
  },
  detailContent: {
    flex: 1,
    padding: 16,
  },
  detailOverview: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    backgroundColor: '#f3f4f6',
    padding: 12,
    borderRadius: 10,
    marginBottom: 16,
    gap: 12,
  },
  overviewItem: {
    width: '48%',
  },
  overviewLabel: {
    fontSize: 11,
    marginTop: 4,
  },
  overviewValue: {
    fontSize: 12,
    fontWeight: '600',
    marginTop: 2,
  },
  detailSection: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  sectionContent: {
    fontSize: 14,
    lineHeight: 20,
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  listText: {
    fontSize: 13,
    lineHeight: 18,
    marginLeft: 8,
    flex: 1,
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: '#f3f4f6',
    padding: 12,
    borderRadius: 10,
  },
  statItemDetail: {
    alignItems: 'center',
    flex: 1,
  },
  statNumber: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  statLabel: {
    fontSize: 10,
    marginTop: 4,
  },
  detailFooter: {
    flexDirection: 'row',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  saveDetailButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    marginRight: 12,
  },
  saveDetailText: {
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },
  appliedDetailButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#10b981',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    flex: 1,
    justifyContent: 'center',
  },
  appliedDetailText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },
  applyDetailButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#667eea',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    flex: 1,
    justifyContent: 'center',
  },
  applyDetailText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    marginRight: 6,
  },
  fab: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 6,
  },
  fabGradient: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  fabBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: '#ef4444',
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fabBadgeText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: 'bold',
  },
});

export default JobBoard;